<template>
  <section>
    <div class="px-4 pb-4 mx-auto max-w-3xl lg:px-0 lg:pb-8">
      <div class="flex flex-col gap-3 w-full">
        <div class="inline-flex w-fit items-start rounded border border-[#E0D5C5] bg-[#F0E8D8] px-3.5 py-1.5">
          <span
            class="font-ww-regular text-xs uppercase tracking-wide text-[#6B3A22]"
            v-html="badgeText"
          />
        </div>

        <h2
          class="mt-2 font-ww-bold text-22 leading-27 text-[#3B2314]"
          v-html="title"
        />

        <div
          class="mt-1 pt-0.5 font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_p+p]:mt-5"
          v-html="introContent"
        />

        <ul class="grid grid-cols-1 gap-2.5 pt-2 pb-6 mt-2 sm:grid-cols-2">
          <li
            v-for="(benefit, index) in benefits"
            :key="index"
            class="flex min-h-14 items-center gap-2.5 rounded-md border border-[#E0D5C5] bg-[#FAF6F0] px-4 py-3"
          >
            <span class="font-ww-regular text-sm leading-6 text-[#F1574B]">✓</span>
            <span
              class="font-ww-regular text-sm leading-7 text-[#4A4A4A]"
              v-html="benefit.text"
            />
          </li>
        </ul>

        <div class="flex flex-col gap-6 items-center">
          <div class="flex flex-col gap-6 w-full lg:flex-row lg:gap-6">
            <div
              class="flex-1 font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_em]:italic [&_p+p]:mt-5 [&_strong]:font-ww-bold [&_sup]:text-xs"
              v-html="bodyContent"
            />

            <div class="relative mx-auto w-full max-w-xs shrink-0 lg:mx-0">
              <nuxt-img
                v-if="imageSrc"
                :src="imageSrc"
                :alt="imageAlt"
                width="320"
                height="275"
                format="webp"
                loading="lazy"
                sizes="xs:100vw sm:320px lg:320px"
                class="object-cover w-full h-72 rounded-2xl"
              />
              <div
                v-else
                class="h-72 w-full rounded-2xl bg-[#2A2A2A]"
                aria-hidden="true"
              />
            </div>
          </div>

          <div class="w-full rounded-2xl bg-[#E8F0E6] px-6 py-6">
            <div
              class="text-center font-ww-bold text-22 leading-normal tracking-tight [&_p+p]:mt-0"
              v-html="highlightContent"
            />
          </div>

          <div
            class="w-full font-ww-regular text-17 leading-6 text-[#4A4A4A] [&_p+p]:mt-5"
            v-html="closingContent"
          />
        </div>
      </div>

      <div class="mt-10 w-full h-px bg-lifeboost-gray" />
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeText: {
    type: String,
    default: 'Supplement Focus'
  },
  title: {
    type: String,
    default: 'Nitric Oxide: The Molecule Men Over 40 Need Most'
  },
  introContent: {
    type: String,
    default: `<p>Nitric oxide (NO) is one of the most important molecules for male cardiovascular health.</p>
<p>In many studies, it has been shown to help:</p>`
  },
  benefits: {
    type: Array,
    default: () => [
      { text: 'Relax blood vessels' },
      { text: 'Enhance oxygen delivery' },
      { text: 'Improve circulation' },
      { text: 'Improve athletic performance' },
      { text: 'Support healthy blood pressure' },
      { text: 'Support erectile function' }
    ]
  },
  bodyContent: {
    type: String,
    default: `<p>But nitric oxide production <strong>declines with age,</strong> especially in men.</p>
<p>That's why many men turn to nitric-oxide-supporting supplements — and research backs them up.</p>
<p>A 2016 study in <em>The Journal of Clinical Hypertension</em> found that boosting nitric oxide levels can significantly improve vascular function and reduce blood pressure.<sup>9</sup></p>`
  },
  imageSrc: {
    type: String,
    default: 'assets/images/img-right-side-mens.png'
  },
  imageAlt: {
    type: String,
    default: 'Nitric oxide molecular model'
  },
  highlightContent: {
    type: String,
    default: `<p class="text-[#105857]">Better circulation</p>
<p class="text-[#105857]">=</p>
<p class="text-[#F1574B]">Better heart health, Better performance,</p>
<p class="text-[#F1574B]">and Better vitality.</p>`
  },
  closingContent: {
    type: String,
    default: `<p>Some men incorporate nitric oxide supplements to help support improved athletic performance as well, as these boosts also help your body to deliver oxygen through your blood to your muscles faster.</p>
<p>With the same benefit in mind, some have even found nitric oxide supplementation to aid in erectile function.</p>
<p>So, while there are potentially added benefits associated with its use, each area of support evidenced in nitric oxide supplementation boils down to one primary potential benefit - improved heart health.</p>`
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Supplement Focus Nitric Oxide',
  inputs: [
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Supplement Focus'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Nitric Oxide: The Molecule Men Over 40 Need Most'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Nitric oxide (NO) is one of the most important molecules for male cardiovascular health.</p>
<p>In many studies, it has been shown to help:</p>`
    },
    {
      name: 'benefits',
      type: 'list',
      defaultValue: [
        { text: 'Relax blood vessels' },
        { text: 'Enhance oxygen delivery' },
        { text: 'Improve circulation' },
        { text: 'Improve athletic performance' },
        { text: 'Support healthy blood pressure' },
        { text: 'Support erectile function' }
      ],
      subFields: [
        {
          name: 'text',
          type: 'text',
          defaultValue: 'Benefit text'
        }
      ]
    },
    {
      name: 'bodyContent',
      type: 'richText',
      defaultValue: `<p>But nitric oxide production <strong>declines with age,</strong> especially in men.</p>
<p>That's why many men turn to nitric-oxide-supporting supplements — and research backs them up.</p>
<p>A 2016 study in <em>The Journal of Clinical Hypertension</em> found that boosting nitric oxide levels can significantly improve vascular function and reduce blood pressure.<sup>9</sup></p>`
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: 'assets/images/img-right-side-mens.png'
    },
    {
      name: 'imageAlt',
      type: 'text',
      defaultValue: 'Nitric oxide molecular model'
    },
    {
      name: 'highlightContent',
      type: 'richText',
      defaultValue: `<p class="text-[#105857]">Better circulation</p>
<p class="text-[#105857]">=</p>
<p class="text-[#F1574B]">Better heart health, Better performance,</p>
<p class="text-[#F1574B]">and Better vitality.</p>`
    },
    {
      name: 'closingContent',
      type: 'richText',
      defaultValue: `<p>Some men incorporate nitric oxide supplements to help support improved athletic performance as well, as these boosts also help your body to deliver oxygen through your blood to your muscles faster.</p>
<p>With the same benefit in mind, some have even found nitric oxide supplementation to aid in erectile function.</p>
<p>So, while there are potentially added benefits associated with its use, each area of support evidenced in nitric oxide supplementation boils down to one primary potential benefit - improved heart health.</p>`
    }
  ]
})
</script>
